import { useState, useEffect } from 'react'
import { Portfolio, Project, Contract } from '@/integrations/trimble/types'
import {
  fetchPortfolios,
  fetchProjects,
  fetchContracts,
} from '@/integrations/trimble/trimbleApi'
import {
  useActivityService,
  ActivityCategory,
  ActivitySubCategory,
  ActivityItemType,
} from '@dc-shared-web/hooks/api/activity'

interface UseTrimbleDataProps {
  isOpen: boolean
  crunchUUID?: string
  documentName?: string
}

/**
 * Custom hook for fetching and managing Trimble data (portfolios, projects, contracts)
 * Handles loading states, error handling, and activity tracking
 */
export const useTrimbleData = ({
  isOpen,
  crunchUUID,
  documentName,
}: UseTrimbleDataProps) => {
  // State for data
  const [portfolios, setPortfolios] = useState<Portfolio[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [contracts, setContracts] = useState<Contract[]>([])

  // State for loading
  const [loadingPortfolios, setLoadingPortfolios] = useState(false)
  const [loadingProjects, setLoadingProjects] = useState(false)
  const [loadingContracts, setLoadingContracts] = useState(false)

  // Selected values
  const [selectedPortfolio, setSelectedPortfolio] = useState<string>('')
  const [selectedProject, setSelectedProject] = useState<string>('')
  const [selectedContract, setSelectedContract] = useState<string>('')

  const { trackActivity } = useActivityService()

  // Track activity helper function
  const trackTrimbleActivity = (
    event: string,
    success: boolean = true,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    additionalData: Record<string, any> = {}
  ) => {
    trackActivity(ActivityCategory.CRUNCH, ActivitySubCategory.DOWNLOADED, {
      itemGuid: crunchUUID || '',
      itemType: ActivityItemType.CRUNCH,
      success,
      data: {
        download_type: 'projectsight',
        document_name: documentName || '',
        event,
        ...additionalData,
      },
    })
  }

  // Fetch portfolios when modal is opened
  useEffect(() => {
    if (isOpen) {
      // Track modal open event
      trackTrimbleActivity('modal_opened')

      const getPortfolios = async () => {
        try {
          setLoadingPortfolios(true)
          const data = await fetchPortfolios()
          setPortfolios(data)
        } catch (error) {
          console.error('Error fetching portfolios:', error)
        } finally {
          setLoadingPortfolios(false)
        }
      }

      getPortfolios()
    } else {
      // Reset state when modal is closed
      setSelectedPortfolio('')
      setSelectedProject('')
      setSelectedContract('')
      setProjects([])
      setContracts([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Fetch projects when portfolio changes
  useEffect(() => {
    if (selectedPortfolio) {
      // Track portfolio selection event
      trackTrimbleActivity('portfolio_selected', true, {
        portfolio_id: selectedPortfolio,
      })

      const getProjects = async () => {
        try {
          setLoadingProjects(true)
          setSelectedProject('') // Reset project selection
          setSelectedContract('') // Reset contract selection
          const data = await fetchProjects(selectedPortfolio)
          setProjects(data)
        } catch (error) {
          console.error('Error fetching projects:', error)
        } finally {
          setLoadingProjects(false)
        }
      }

      getProjects()
    } else {
      setProjects([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPortfolio])

  // Fetch contracts when project changes
  useEffect(() => {
    if (selectedPortfolio && selectedProject) {
      // Track project selection event
      trackTrimbleActivity('project_selected', true, {
        portfolio_id: selectedPortfolio,
        project_id: selectedProject,
      })

      const getContracts = async () => {
        try {
          setLoadingContracts(true)
          setSelectedContract('') // Reset contract selection
          const data = await fetchContracts(selectedPortfolio, selectedProject)
          setContracts(data)

          // Track if project has no contracts
          if (data.length === 0) {
            trackTrimbleActivity('no_contracts_available', false, {
              portfolio_id: selectedPortfolio,
              project_id: selectedProject,
              error_message: 'Project has no contracts available',
            })
          }
        } catch (error) {
          console.error('Error fetching contracts:', error)
        } finally {
          setLoadingContracts(false)
        }
      }

      getContracts()
    } else {
      setContracts([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPortfolio, selectedProject])

  // Track contract selection
  useEffect(() => {
    if (selectedContract) {
      trackTrimbleActivity('contract_selected', true, {
        portfolio_id: selectedPortfolio,
        project_id: selectedProject,
        contract_id: selectedContract,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContract])

  // Track modal closed event if user had started selecting options
  useEffect(() => {
    if (!isOpen && (selectedPortfolio || selectedProject || selectedContract)) {
      trackTrimbleActivity('modal_closed_after_selection', true, {
        portfolio_selected: !!selectedPortfolio,
        project_selected: !!selectedProject,
        contract_selected: !!selectedContract,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedPortfolio, selectedProject, selectedContract])

  return {
    // Data
    portfolios,
    projects,
    contracts,

    // Loading states
    loadingPortfolios,
    loadingProjects,
    loadingContracts,

    // Selected values
    selectedPortfolio,
    selectedProject,
    selectedContract,

    // Setters
    setSelectedPortfolio,
    setSelectedProject,
    setSelectedContract,

    // Reset function
    reset: () => {
      setSelectedPortfolio('')
      setSelectedProject('')
      setSelectedContract('')
      setProjects([])
      setContracts([])
    },

    // Activity tracking
    trackTrimbleActivity,
  }
}

export default useTrimbleData
